// StorySystem.ts - 故事/任务系统
// 管理主线任务、支线任务、记忆碎片收集

import { GameState } from './GameState.js';
import { EventSystem } from './EventSystem.js';

// 任务类型
export type TaskType = 'main' | 'side' | 'memory';

// 任务状态
export type TaskStatus = 'locked' | 'available' | 'active' | 'completed';

// 任务目标
export interface TaskObjective {
  id: string;
  type: 'interact' | 'goto' | 'collect' | 'talk' | 'complete_action';
  target: string;
  targetLabel: string;
  current: number;
  required: number;
  description: string;
}

// 任务定义
export interface Task {
  id: string;
  type: TaskType;
  title: string;
  description: string;
  objectives: TaskObjective[];
  rewards: {
    memoryPoints?: number;
    coins?: number;
    flags?: string[];
  };
  status: TaskStatus;
  prerequisite?: string; // 前置任务ID
  unlocks?: string[]; // 完成后解锁的任务
  hint: string; // 任务提示
}

// 记忆碎片
export interface MemoryFragment {
  id: string;
  title: string;
  content: string;
  source: string; // 来源：奶奶、爷爷、丹丹、邻居等
  unlocked: boolean;
  memoryPoints: number;
  prerequisite?: string;
}

// 成长阶段
export interface GrowthStage {
  name: string;
  minMemoryPoints: number;
  description: string;
  unlockedFeatures: string[];
}

// 主线任务数据
export const MAIN_TASKS: Task[] = [
  {
    id: 'main_01_new_day',
    type: 'main',
    title: '新的一天',
    description: '奶奶叫你起床了，先熟悉一下家里吧。',
    objectives: [
      { id: 'obj_move', type: 'complete_action', target: 'move', targetLabel: '移动', current: 0, required: 3, description: '点击地面移动欢欢 3 次' },
      { id: 'obj_look', type: 'complete_action', target: 'look', targetLabel: '观察', current: 0, required: 2, description: '点击场景中的物体 2 次' }
    ],
    rewards: { memoryPoints: 5, coins: 10 },
    status: 'available',
    unlocks: ['main_02_help_grandma'],
    hint: '点击地面让欢欢走动，点击物体可以互动'
  },
  {
    id: 'main_02_help_grandma',
    type: 'main',
    title: '帮奶奶做事',
    description: '奶奶年纪大了，帮她做一些力所能及的事情吧。',
    objectives: [
      { id: 'obj_feed_chicken', type: 'complete_action', target: 'feed_chicken', targetLabel: '喂鸡', current: 0, required: 1, description: '去鸡窝喂鸡' },
      { id: 'obj_sweep', type: 'complete_action', target: 'sweep', targetLabel: '扫地', current: 0, required: 1, description: '在院子里扫地' },
      { id: 'obj_talk_grandma', type: 'talk', target: 'grandma', targetLabel: '奶奶', current: 0, required: 1, description: '和奶奶对话' }
    ],
    rewards: { memoryPoints: 10, coins: 20 },
    status: 'locked',
    prerequisite: 'main_01_new_day',
    unlocks: ['main_03_grandpa_story'],
    hint: '找到鸡窝和扫帚，点击进行互动'
  },
  {
    id: 'main_03_grandpa_story',
    type: 'main',
    title: '听爷爷讲故事',
    description: '爷爷有很多过去的故事，去听听吧。',
    objectives: [
      { id: 'obj_talk_daye', type: 'talk', target: 'daye', targetLabel: '爷爷', current: 0, required: 1, description: '和爷爷对话' },
      { id: 'obj_listen_story', type: 'complete_action', target: 'listen_story', targetLabel: '听故事', current: 0, required: 1, description: '完整听一个故事' }
    ],
    rewards: { memoryPoints: 15, coins: 15, flags: ['memory_fragment_01'] },
    status: 'locked',
    prerequisite: 'main_02_help_grandma',
    unlocks: ['main_04_dandan_friend'],
    hint: '爷爷通常在竹椅附近，点击和他对话'
  },
  {
    id: 'main_04_dandan_friend',
    type: 'main',
    title: '丹丹的朋友',
    description: '丹丹是欢欢最好的朋友，去和它玩一玩。',
    objectives: [
      { id: 'obj_play_dandan', type: 'complete_action', target: 'play_dandan', targetLabel: '逗丹丹', current: 0, required: 2, description: '逗丹丹玩 2 次' },
      { id: 'obj_pet_dandan', type: 'complete_action', target: 'pet_dandan', targetLabel: '抚摸', current: 0, required: 1, description: '抚摸丹丹' }
    ],
    rewards: { memoryPoints: 10, coins: 10 },
    status: 'locked',
    prerequisite: 'main_03_grandpa_story',
    unlocks: ['main_05_neighbor_visit'],
    hint: '点击丹丹可以和它互动'
  },
  {
    id: 'main_05_neighbor_visit',
    type: 'main',
    title: '邻居来访',
    description: '邻居王婶来串门了，去和她打个招呼。',
    objectives: [
      { id: 'obj_greet_neighbor', type: 'talk', target: 'neighbor', targetLabel: '邻居', current: 0, required: 1, description: '和来访的邻居对话' },
      { id: 'obj_chat_topics', type: 'complete_action', target: 'chat_topic', targetLabel: '话题', current: 0, required: 2, description: '聊 2 个不同的话题' }
    ],
    rewards: { memoryPoints: 15, coins: 25 },
    status: 'locked',
    prerequisite: 'main_04_dandan_friend',
    unlocks: ['side_memory_collection'],
    hint: '邻居来访时会有提示，点击邻居进行对话'
  }
];

// 支线任务数据
export const SIDE_TASKS: Task[] = [
  {
    id: 'side_memory_collection',
    type: 'side',
    title: '收集记忆碎片',
    description: '和家人多聊天，收集珍贵的回忆。',
    objectives: [
      { id: 'obj_collect_fragments', type: 'complete_action', target: 'memory_fragment', targetLabel: '记忆碎片', current: 0, required: 3, description: '收集 3 个记忆碎片' }
    ],
    rewards: { memoryPoints: 20, coins: 30 },
    status: 'locked',
    hint: '和奶奶、爷爷、丹丹多对话，可能会触发回忆'
  },
  {
    id: 'side_care_yard',
    type: 'side',
    title: '照顾院子',
    description: '让院子保持整洁，花草茁壮成长。',
    objectives: [
      { id: 'obj_water_flowers', type: 'complete_action', target: 'water_flower', targetLabel: '浇花', current: 0, required: 3, description: '浇花 3 次' },
      { id: 'obj_chop_wood', type: 'complete_action', target: 'chop_wood', targetLabel: '劈柴', current: 0, required: 2, description: '劈柴 2 次' },
      { id: 'obj_tidy', type: 'complete_action', target: 'tidy', targetLabel: '整理', current: 0, required: 2, description: '整理院子 2 次' }
    ],
    rewards: { memoryPoints: 15, coins: 25 },
    status: 'available',
    hint: '点击花丛、柴堆、杂物可以进行相应的动作'
  },
  {
    id: 'side_family_past',
    type: 'side',
    title: '了解家人过去',
    description: '听听家人年轻时的故事。',
    objectives: [
      { id: 'obj_grandma_past', type: 'talk', target: 'grandma', targetLabel: '奶奶', current: 0, required: 3, description: '听奶奶讲过去的故事' },
      { id: 'obj_daye_past', type: 'talk', target: 'daye', targetLabel: '爷爷', current: 0, required: 2, description: '听爷爷讲过去的故事' }
    ],
    rewards: { memoryPoints: 25, coins: 20 },
    status: 'available',
    hint: '多次和奶奶、爷爷对话，选择听故事的选项'
  }
];

// 记忆碎片数据
export const MEMORY_FRAGMENTS: MemoryFragment[] = [
  {
    id: 'frag_01',
    title: '老槐树的秘密',
    content: '奶奶说，这棵老槐树比她年纪还大。小时候她经常在树下玩耍，那时候她的奶奶也坐在这棵树下给她讲故事...',
    source: '奶奶',
    unlocked: false,
    memoryPoints: 5
  },
  {
    id: 'frag_02',
    title: '爷爷的烟斗',
    content: '爷爷抽了四十年的旱烟，那个烟斗还是太爷爷传下来的。他说每次抽烟，都能想起年轻时候的事...',
    source: '爷爷',
    unlocked: false,
    memoryPoints: 5
  },
  {
    id: 'frag_03',
    title: '丹丹的到来',
    content: '那是一个下雨天，你听到草丛里有声音。扒开草丛，一只瑟瑟发抖的小黄狗正看着你...',
    source: '系统',
    unlocked: false,
    memoryPoints: 10
  },
  {
    id: 'frag_04',
    title: '妈妈的童年',
    content: '你妈妈小时候也住在这里。她那时候最调皮，经常爬到那棵老槐树上，怎么叫都不下来...',
    source: '奶奶',
    unlocked: false,
    memoryPoints: 8,
    prerequisite: 'frag_01'
  },
  {
    id: 'frag_05',
    title: '爸爸的农活',
    content: '你爸爸年轻的时候，天不亮就下地干活。那时候没有机器，全靠一双手。但他从来不叫苦...',
    source: '爷爷',
    unlocked: false,
    memoryPoints: 8,
    prerequisite: 'frag_02'
  },
  {
    id: 'frag_06',
    title: '第一次相遇',
    content: '那是你六岁那年春天，在放牛山脚下。你永远不会忘记丹丹那双湿漉漉的眼睛...',
    source: '系统',
    unlocked: false,
    memoryPoints: 10,
    prerequisite: 'frag_03'
  },
  {
    id: 'frag_07',
    title: '石磨的回忆',
    content: '那座石磨以前是用来磨豆腐的。每年过年，全村的人都来借石磨做豆腐，那是最热闹的时候...',
    source: '奶奶',
    unlocked: false,
    memoryPoints: 6
  },
  {
    id: 'frag_08',
    title: '井水甘甜',
    content: '这口井已经有上百年历史了。无论旱涝，井水从不干涸。村里人都说这井有神灵保佑...',
    source: '爷爷',
    unlocked: false,
    memoryPoints: 6
  }
];

// 成长阶段
export const GROWTH_STAGES: GrowthStage[] = [
  {
    name: '懵懂童年',
    minMemoryPoints: 0,
    description: '刚刚开始的童年时光',
    unlockedFeatures: ['基本互动']
  },
  {
    name: '好奇少年',
    minMemoryPoints: 30,
    description: '开始探索世界，对一切都充满好奇',
    unlockedFeatures: ['更多对话选项', '室内场景']
  },
  {
    name: '懂事孩子',
    minMemoryPoints: 70,
    description: '开始懂得关心家人，承担一些责任',
    unlockedFeatures: ['帮家人做事', '深度对话']
  },
  {
    name: '回忆收藏家',
    minMemoryPoints: 120,
    description: '收集了许多珍贵的回忆',
    unlockedFeatures: ['特殊回忆', '隐藏故事']
  },
  {
    name: '故事讲述者',
    minMemoryPoints: 200,
    description: '你已经成为了一个有故事的人',
    unlockedFeatures: ['完整故事线', '特别结局']
  }
];

export class StorySystem {
  private tasks: Map<string, Task> = new Map();
  private memoryFragments: Map<string, MemoryFragment> = new Map();
  private completedTasks: Set<string> = new Set();
  private unlockedFragments: Set<string> = new Set();
  private totalMemoryPoints: number = 0;
  private gameState: GameState;
  private currentTaskId: string | null = null;
  private onTaskUpdate?: () => void;
  private onMemoryUnlock?: (fragment: MemoryFragment) => void;

  constructor(gameState: GameState) {
    this.gameState = gameState;
    this.loadTasks();
    this.loadMemoryFragments();
  }

  // 加载任务
  private loadTasks(): void {
    [...MAIN_TASKS, ...SIDE_TASKS].forEach(task => {
      this.tasks.set(task.id, { ...task });
    });
    
    // 恢复保存的状态
    const savedTasks = this.gameState.getPath('storySystem.completedTasks') as string[] | undefined;
    if (savedTasks) {
      savedTasks.forEach(id => {
        this.completedTasks.add(id);
        const task = this.tasks.get(id);
        if (task) task.status = 'completed';
      });
    }
    
    // 更新任务解锁状态
    this.updateTaskUnlocks();
  }

  // 加载记忆碎片
  private loadMemoryFragments(): void {
    MEMORY_FRAGMENTS.forEach(frag => {
      this.memoryFragments.set(frag.id, { ...frag });
    });
    
    // 恢复保存的状态
    const savedFragments = this.gameState.getPath('storySystem.unlockedFragments') as string[] | undefined;
    if (savedFragments) {
      savedFragments.forEach(id => {
        this.unlockFragment(id, false);
      });
    }
    
    this.totalMemoryPoints = this.gameState.getPath('storySystem.totalMemoryPoints') as number || 0;
  }

  // 更新任务解锁状态
  private updateTaskUnlocks(): void {
    this.tasks.forEach(task => {
      if (task.status === 'locked' && task.prerequisite) {
        if (this.completedTasks.has(task.prerequisite)) {
          task.status = 'available';
        }
      }
    });
  }

  // 获取当前任务
  getCurrentTask(): Task | null {
    if (this.currentTaskId) {
      return this.tasks.get(this.currentTaskId) || null;
    }
    // 返回第一个可用的主线任务
    for (const task of MAIN_TASKS) {
      const t = this.tasks.get(task.id);
      if (t && (t.status === 'available' || t.status === 'active')) {
        return t;
      }
    }
    return null;
  }

  // 接受任务
  acceptTask(taskId: string): boolean {
    const task = this.tasks.get(taskId);
    if (!task || task.status !== 'available') return false;
    
    task.status = 'active';
    this.currentTaskId = taskId;
    this.notifyTaskUpdate();
    return true;
  }

  // 更新任务进度
  updateObjective(taskId: string, objectiveId: string, progress: number = 1): void {
    const task = this.tasks.get(taskId);
    if (!task || task.status !== 'active') return;
    
    const objective = task.objectives.find(o => o.id === objectiveId);
    if (!objective || objective.current >= objective.required) return;
    
    objective.current = Math.min(objective.current + progress, objective.required);
    this.notifyTaskUpdate();
    
    // 检查任务是否完成
    if (task.objectives.every(o => o.current >= o.required)) {
      this.completeTask(taskId);
    }
    
    // 保存进度
    this.saveProgress();
  }

  // 检查并更新目标进度（通过类型和目标）
  checkAndUpdateObjective(type: string, target: string): void {
    const currentTask = this.getCurrentTask();
    if (!currentTask) return;
    
    currentTask.objectives.forEach(obj => {
      if (obj.type === type && obj.target === target && obj.current < obj.required) {
        this.updateObjective(currentTask.id, obj.id, 1);
      }
    });
  }

  // 完成任务
  completeTask(taskId: string): void {
    const task = this.tasks.get(taskId);
    if (!task || task.status === 'completed') return;
    
    task.status = 'completed';
    this.completedTasks.add(taskId);
    
    // 发放奖励
    if (task.rewards.memoryPoints) {
      this.addMemoryPoints(task.rewards.memoryPoints);
    }
    
    // 解锁后续任务
    if (task.unlocks) {
      task.unlocks.forEach(unlockId => {
        const unlockTask = this.tasks.get(unlockId);
        if (unlockTask && unlockTask.status === 'locked') {
          unlockTask.status = 'available';
        }
      });
    }
    
    // 解锁记忆碎片
    if (task.rewards.flags) {
      task.rewards.flags.forEach(flag => {
        if (flag.startsWith('memory_fragment_')) {
          const fragId = flag.replace('memory_fragment_', 'frag_');
          this.unlockFragment(fragId);
        }
      });
    }
    
    // 切换到下一个任务
    if (this.currentTaskId === taskId) {
      this.currentTaskId = null;
    }
    
    this.notifyTaskUpdate();
    this.saveProgress();
  }

  // 解锁记忆碎片
  unlockFragment(fragmentId: string, notify: boolean = true): boolean {
    const fragment = this.memoryFragments.get(fragmentId);
    if (!fragment || fragment.unlocked) return false;
    
    // 检查前置条件
    if (fragment.prerequisite && !this.unlockedFragments.has(fragment.prerequisite)) {
      return false;
    }
    
    fragment.unlocked = true;
    this.unlockedFragments.add(fragmentId);
    this.addMemoryPoints(fragment.memoryPoints);
    
    if (notify && this.onMemoryUnlock) {
      this.onMemoryUnlock(fragment);
    }
    
    this.saveProgress();
    return true;
  }

  // 添加回忆度
  addMemoryPoints(points: number): void {
    this.totalMemoryPoints += points;
    this.saveProgress();
  }

  // 获取当前成长阶段
  getCurrentGrowthStage(): GrowthStage {
    for (let i = GROWTH_STAGES.length - 1; i >= 0; i--) {
      if (this.totalMemoryPoints >= GROWTH_STAGES[i].minMemoryPoints) {
        return GROWTH_STAGES[i];
      }
    }
    return GROWTH_STAGES[0];
  }

  // 获取下一个成长阶段
  getNextGrowthStage(): GrowthStage | null {
    const current = this.getCurrentGrowthStage();
    const currentIndex = GROWTH_STAGES.indexOf(current);
    return GROWTH_STAGES[currentIndex + 1] || null;
  }

  // 获取到下一阶段的进度
  getProgressToNextStage(): number {
    const current = this.getCurrentGrowthStage();
    const next = this.getNextGrowthStage();
    
    if (!next) return 100;
    
    const currentMin = current.minMemoryPoints;
    const nextMin = next.minMemoryPoints;
    const progress = (this.totalMemoryPoints - currentMin) / (nextMin - currentMin);
    return Math.min(100, Math.max(0, progress * 100));
  }

  // 保存进度
  private saveProgress(): void {
    this.gameState.setPath('storySystem.completedTasks', Array.from(this.completedTasks));
    this.gameState.setPath('storySystem.unlockedFragments', Array.from(this.unlockedFragments));
    this.gameState.setPath('storySystem.totalMemoryPoints', this.totalMemoryPoints);
  }

  // 设置回调
  setCallbacks(callbacks: {
    onTaskUpdate?: () => void;
    onMemoryUnlock?: (fragment: MemoryFragment) => void;
  }): void {
    this.onTaskUpdate = callbacks.onTaskUpdate;
    this.onMemoryUnlock = callbacks.onMemoryUnlock;
  }

  private notifyTaskUpdate(): void {
    if (this.onTaskUpdate) this.onTaskUpdate();
  }

  // Getters
  getTotalMemoryPoints(): number { return this.totalMemoryPoints; }
  getUnlockedFragments(): MemoryFragment[] {
    return Array.from(this.memoryFragments.values()).filter(f => f.unlocked);
  }
  getAllFragments(): MemoryFragment[] {
    return Array.from(this.memoryFragments.values());
  }
  getAllTasks(): Task[] {
    return Array.from(this.tasks.values());
  }
  getCompletedTasksCount(): number {
    return this.completedTasks.size;
  }
}